import { Client } from "@stomp/stompjs";
import { authService } from "services";

/**
 * globalSocketService — V3: Native WebSocket + STOMP
 *
 * Changes from V2:
 *  - SockJS removed entirely → native WebSocket via brokerURL
 *  - Heartbeats sent from a Web Worker (immune to background-tab throttling)
 *  - Exponential backoff reconnection with max attempts
 *  - visibilitychange listener for tab-resume reconnection
 *  - Event system for consumers to react to connection state changes
 */

const HEARTBEAT_INTERVAL = 10_000;
const HEARTBEAT_INCOMING = 15_000;
const HEARTBEAT_OUTGOING = 15_000;
const INITIAL_RECONNECT_DELAY = 2_000;
const MAX_RECONNECT_DELAY = 60_000;
// No tope duro: la planificación con IA puede durar horas, mejor seguir
// reintentando con backoff capado (60 s) que dejar el cliente parado.
// Los consumidores (p. ej. plansSocketService) tienen polling REST como
// respaldo, así que el coste de seguir reintentando es bajo.
const MAX_RECONNECT_ATTEMPTS = Infinity;

export const SESSION_ID = Math.random().toString(36).substring(7).toUpperCase();

// ─── State ──────────────────────────────────────────────────────────────────────
let stompClient = null;
const subscriptions = new Map();
const pendingTopics = new Set();
let intentionalDisconnect = false;
let connectResolvers = [];
let reconnectAttempts = 0;
let heartbeatWorker = null;
const eventListeners = new Map();

// ─── URL conversion ─────────────────────────────────────────────────────────────
// Spring Boot + SockJS exposes raw WS at {endpoint}/websocket
// Convert: http(s)://host/apiws/ → ws(s)://host/apiws/websocket

function toBrokerURL(httpUrl) {
	let url = httpUrl.replace(/^http/, "ws");
	url = url.replace(/\/+$/, "");
	if (!url.endsWith("/websocket")) {
		url += "/websocket";
	}
	return url;
}

// ─── Heartbeat Web Worker ───────────────────────────────────────────────────────
// Web Worker timers are NOT subject to background-tab throttling.
// The library also sends its own heartbeats, but those get throttled in bg tabs.
// This Worker ensures at least one heartbeat reaches the server every interval.

function createHeartbeatWorker() {
	const code = [
		"let tid = null;",
		"self.onmessage = function(e) {",
		"  if (e.data.command === 'start') {",
		"    if (tid) clearInterval(tid);",
		"    tid = setInterval(function() { self.postMessage('tick'); }, e.data.interval || 10000);",
		"  } else if (e.data.command === 'stop') {",
		"    if (tid) { clearInterval(tid); tid = null; }",
		"  }",
		"};",
	].join("\n");
	const blob = new Blob([code], { type: "application/javascript" });
	return new Worker(URL.createObjectURL(blob));
}

function startHeartbeatWorker() {
	if (heartbeatWorker) return;
	try {
		heartbeatWorker = createHeartbeatWorker();
		heartbeatWorker.onmessage = () => {
			if (stompClient?.connected && stompClient.webSocket?.readyState === WebSocket.OPEN) {
				try {
					stompClient.webSocket.send("\n");
				} catch (_) { /* noop */ }
			}
		};
		heartbeatWorker.postMessage({ command: "start", interval: HEARTBEAT_INTERVAL });
	} catch (e) {
		console.warn(`[WS:${SESSION_ID}] Web Worker not available, relying on library heartbeats only`);
	}
}

function stopHeartbeatWorker() {
	if (heartbeatWorker) {
		heartbeatWorker.postMessage({ command: "stop" });
		heartbeatWorker.terminate();
		heartbeatWorker = null;
	}
}

// ─── Event system ───────────────────────────────────────────────────────────────
// Consumers can listen for: 'connected', 'reconnected', 'disconnected',
// 'tab-visible', 'max-reconnects-reached'

function emit(event, data) {
	const listeners = eventListeners.get(event);
	if (listeners) {
		listeners.forEach((fn) => {
			try { fn(data); } catch (_) { /* noop */ }
		});
	}
}

function on(event, fn) {
	if (!eventListeners.has(event)) eventListeners.set(event, new Set());
	eventListeners.get(event).add(fn);
	return () => eventListeners.get(event)?.delete(fn);
}

// ─── Visibility change ─────────────────────────────────────────────────────────
// When the tab comes back to foreground, check and reconnect if needed.

function handleVisibilityChange() {
	if (document.visibilityState !== "visible") return;
	if (intentionalDisconnect) return;

	// Siempre emitir el evento — los consumidores (watchers de IA, etc.) lo
	// usan para hacer un check de respaldo aunque el WS parezca sano.
	emit("tab-visible");

	if (!stompClient) return;

	// Si el WS está inactivo o no conectado, forzar reconexión inmediata.
	const wsClosed =
		!stompClient.connected ||
		(stompClient.webSocket &&
			stompClient.webSocket.readyState !== WebSocket.OPEN);

	if (wsClosed) {
		console.log(
			`[WS:${SESSION_ID}] Tab visible — reconnecting (was ${stompClient.active ? "reconnecting" : "inactive"})`
		);
		reconnectAttempts = 0;
		if (!stompClient.active) {
			stompClient.activate();
		}
	}
}

if (typeof document !== "undefined") {
	document.addEventListener("visibilitychange", handleVisibilityChange);
}

// ─── Helpers ────────────────────────────────────────────────────────────────────

function _invalidateStompSubs() {
	for (const subData of subscriptions.values()) {
		subData.stompSub = null;
	}
}

const ALERT_NOTIFICATIONS_PREFIX = "/topic/alert-notifications/";

function resolveAlertNotificationsTopic(topic) {
	if (!topic || !topic.startsWith(ALERT_NOTIFICATIONS_PREFIX)) return topic;
	const jwtEmployeeId = authService.getJwtEmployeeId?.();
	if (jwtEmployeeId == null) return topic;
	return `${ALERT_NOTIFICATIONS_PREFIX}${jwtEmployeeId}`;
}

function remapStaleAlertNotificationSubscriptions() {
	const jwtEmployeeId = authService.getJwtEmployeeId?.();
	if (jwtEmployeeId == null) return;
	const canonical = `${ALERT_NOTIFICATIONS_PREFIX}${jwtEmployeeId}`;
	for (const [topic, subData] of Array.from(subscriptions.entries())) {
		if (!topic.startsWith(ALERT_NOTIFICATIONS_PREFIX) || topic === canonical) {
			continue;
		}
		subscriptions.delete(topic);
		try {
			subData.stompSub?.unsubscribe({ destination: topic });
		} catch (_) {
			/* noop */
		}
		if (!subscriptions.has(canonical)) {
			subscriptions.set(canonical, { ...subData, stompSub: null });
		}
	}
}

// ─── STOMP Client ───────────────────────────────────────────────────────────────

function _createStompClient() {
	const client = new Client({
		brokerURL: toBrokerURL(global.BASE_WS),

		connectHeaders: { jwt_token: authService.token.access_token },

		heartbeatIncoming: HEARTBEAT_INCOMING,
		heartbeatOutgoing: HEARTBEAT_OUTGOING,

		reconnectDelay: INITIAL_RECONNECT_DELAY,

		debug: () => {},

		beforeConnect: () => {
			authService.syncTokenFromStorage?.();
			client.connectHeaders = { jwt_token: authService.token.access_token };
		},

		onConnect: () => {
			const wasReconnect = reconnectAttempts > 0;
			reconnectAttempts = 0;
			client.reconnectDelay = INITIAL_RECONNECT_DELAY;

			console.log(`[WS:${SESSION_ID}] STOMP connected${wasReconnect ? " (reconnect)" : ""}`);

			startHeartbeatWorker();

			const resolvers = connectResolvers.splice(0);
			resolvers.forEach(({ resolve }) => resolve(client));

			// El topic de alertas está atado al employeeId del JWT. Si el token
			// cambió (cambio de venue), no re-suscribir el ID de la otra venue.
			remapStaleAlertNotificationSubscriptions();

			// Always re-subscribe: old stompSubs are dead references from
			// the previous WS connection (onDisconnect may not have fired).
			for (const [topic, subData] of subscriptions.entries()) {
				try {
					const stompSub = client.subscribe(topic, (msg) => {
						try {
							subData.callback(JSON.parse(msg.body));
						} catch (e) {
							console.error(`[WS:${SESSION_ID}] Parse error on ${topic}:`, e);
						}
					});
					subData.stompSub = stompSub;
				} catch (e) {
					console.error(`[WS:${SESSION_ID}] Failed to re-subscribe ${topic}:`, e);
				}
			}

			emit("connected");
			if (wasReconnect) emit("reconnected");
		},

		onDisconnect: () => {
			console.log(`[WS:${SESSION_ID}] STOMP disconnected`);
			_invalidateStompSubs();
			emit("disconnected");
		},

		onStompError: (frame) => {
			const msg = (frame.headers?.message || "").toLowerCase();
			console.error(`[WS:${SESSION_ID}] STOMP error: "${msg}"`);

			const isAuthError =
				msg.includes("unauthorized") || msg.includes("denied") || msg.includes("forbidden");
			if (isAuthError) {
				console.error(`[WS:${SESSION_ID}] Auth error — stopping`);
				disconnect();
				const resolvers = connectResolvers.splice(0);
				resolvers.forEach(({ reject }) => reject(new Error("Auth error: " + msg)));
			}
		},

		onWebSocketError: (event) => {
			console.warn(`[WS:${SESSION_ID}] Transport error`, event?.type);
		},

		onWebSocketClose: (event) => {
			console.log(`[WS:${SESSION_ID}] WS closed (code: ${event?.code})`);

			// Mark all STOMP subs as dead so onConnect always re-subscribes.
			_invalidateStompSubs();

			if (intentionalDisconnect) return;

			reconnectAttempts++;

			// Backoff exponencial capado a 60 s, sin tope duro de intentos.
			// El cliente reintentará indefinidamente hasta que vuelva la red.
			const expDelay =
				INITIAL_RECONNECT_DELAY *
				Math.pow(2, Math.min(reconnectAttempts - 1, 6));
			const delay = Math.min(expDelay, MAX_RECONNECT_DELAY);
			client.reconnectDelay = delay;

			// A partir del intento 15 emitimos el evento para que los
			// consumidores activen su modo "degradado" (polling REST puro)
			// pero NO paramos al cliente: seguirá intentando en background.
			if (reconnectAttempts === 15) {
				emit("max-reconnects-reached");
			}

			console.log(
				`[WS:${SESSION_ID}] Reconnecting in ${delay}ms (attempt ${reconnectAttempts})`
			);
		},
	});

	return client;
}

// ─── Public API ─────────────────────────────────────────────────────────────────

function getClient() {
	return stompClient;
}

function isConnected() {
	return !!(stompClient && stompClient.connected);
}

function isAutoReconnecting() {
	return !!(stompClient && stompClient.active && !stompClient.connected);
}

function connect() {
	intentionalDisconnect = false;

	if (isConnected()) return Promise.resolve(stompClient);

	if (stompClient && stompClient.active) {
		return new Promise((resolve, reject) => {
			connectResolvers.push({ resolve, reject });
		});
	}

	stompClient = _createStompClient();

	const promise = new Promise((resolve, reject) => {
		connectResolvers.push({ resolve, reject });
	});

	stompClient.activate();
	return promise;
}

function disconnect() {
	intentionalDisconnect = true;

	const resolvers = connectResolvers.splice(0);
	resolvers.forEach(({ reject }) => reject(new Error("Intentional disconnect")));

	subscriptions.forEach((subData, topic) => {
		try { subData.stompSub?.unsubscribe({ destination: topic }); } catch (_) { /* noop */ }
	});
	subscriptions.clear();
	pendingTopics.clear();

	stopHeartbeatWorker();

	if (stompClient) {
		try { stompClient.deactivate(); } catch (_) { /* noop */ }
		stompClient = null;
	}

	reconnectAttempts = 0;
	emit("disconnected");
}

async function subscribe(topic, callback) {
	if (!topic) return Promise.reject(new Error("Topic required"));
	if (!callback || typeof callback !== "function")
		return Promise.reject(new Error("Callback required"));

	topic = resolveAlertNotificationsTopic(topic);

	if (subscriptions.has(topic) || pendingTopics.has(topic)) {
		return Promise.resolve(subscriptions.get(topic)?.stompSub);
	}

	pendingTopics.add(topic);

	try {
		subscriptions.set(topic, { stompSub: null, callback });

		if (!isConnected()) await connect();

		let subData = subscriptions.get(topic);
		if (!subData?.stompSub && stompClient?.connected) {
			const stompSub = stompClient.subscribe(topic, (msg) => {
				try {
					callback(JSON.parse(msg.body));
				} catch (e) {
					console.error(`[WS:${SESSION_ID}] Parse error on ${topic}:`, e);
				}
			});
			subscriptions.set(topic, { stompSub, callback });
		}

		pendingTopics.delete(topic);
		return subscriptions.get(topic)?.stompSub;
	} catch (error) {
		pendingTopics.delete(topic);
		subscriptions.delete(topic);
		throw error;
	}
}

function unsubscribe(topic) {
	if (!topic) return;
	const subData = subscriptions.get(topic);
	if (subData) {
		try { subData.stompSub?.unsubscribe({ destination: topic }); } catch (_) { /* noop */ }
		subscriptions.delete(topic);
	}
}

function getActiveSubscriptions() {
	return Array.from(subscriptions.keys());
}

// ─── Export ─────────────────────────────────────────────────────────────────────

export const globalSocketService = {
	connect,
	disconnect,
	isConnected,
	isAutoReconnecting,
	getClient,
	subscribe,
	unsubscribe,
	getActiveSubscriptions,
	on,
};
